import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { WebauthnCredentialDto, WebauthnRegistrationOptionsDto } from '@fas/types';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, Field, Input, Spinner } from '@fas/ui';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { createCredential, platformAuthenticatorAvailable } from '@/lib/webauthn';
import { suggestedDeviceName } from './DevicesCard';

const MY_WEBAUTHN_KEY = ['/webauthn', 'mine'] as const;

/** One's own fingerprint or face sign-ins (Phase 3H), for the clock dialog too. */
export function useMyWebauthnCredentials(enabled = true) {
  return useQuery({ queryKey: MY_WEBAUTHN_KEY, queryFn: () => api.get<WebauthnCredentialDto[]>('/webauthn/credentials/mine'), enabled, staleTime: 60_000 });
}

/**
 * Fingerprint, face or device PIN at clock events (Phase 3H, ADR-063). The device verifies its
 * user itself and sends only a signature: no fingerprint or face ever reaches the server. An
 * attendance policy may flag or require it.
 */
export function BiometricsCard() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const credentials = useMyWebauthnCredentials();
  const available = useQuery({ queryKey: ['webauthn-platform'], queryFn: platformAuthenticatorAvailable, staleTime: Infinity });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(suggestedDeviceName);
  const [removing, setRemoving] = useState<WebauthnCredentialDto | null>(null);

  // Links from the clock dialog open this card (/account#biometrics).
  useEffect(() => {
    if (location.hash === '#biometrics') document.getElementById('biometrics')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['/webauthn'] });
  const setUp = useMutation({
    mutationFn: async () => {
      const options = await api.post<WebauthnRegistrationOptionsDto>('/webauthn/registration-options');
      const credential = await createCredential(options);
      return api.post<WebauthnCredentialDto>('/webauthn/credentials', { label: name.trim(), credential });
    },
    onSuccess: async () => {
      toast.success('Fingerprint or face confirmation is set up on this device');
      setAdding(false);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.post<WebauthnCredentialDto>(`/webauthn/credentials/mine/${id}/remove`),
    onSuccess: async () => {
      toast.success('Removed');
      setRemoving(null);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const live = credentials.data?.filter((c) => c.status === 'ACTIVE') ?? [];
  const removed = credentials.data?.filter((c) => c.status === 'REVOKED') ?? [];

  return (
    <Card id="biometrics">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Fingerprint className="size-5" aria-hidden /> Fingerprint or face
        </CardTitle>
        <CardDescription>
          Confirm it is you with your device’s fingerprint, face or PIN unlock when you clock in or out. Your fingerprint or face never leaves the device: it only signs a
          confirmation. Your attendance policy may ask for it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {credentials.isLoading || available.isLoading ? <Spinner /> : null}
        {credentials.error ? <Alert tone="danger">{errorMessage(credentials.error)}</Alert> : null}
        {available.data === false ? (
          <Alert tone="warning" title="This device cannot confirm you">
            It has no fingerprint, face or PIN unlock that the browser can use, or the browser does not support it. Set it up on your phone instead.
          </Alert>
        ) : null}

        {live.length > 0 ? (
          <ul className="divide-y divide-slate-100" aria-label="Fingerprint or face sign-ins">
            {live.map((c) => (
              <li key={c.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm">
                  <p className="flex flex-wrap items-center gap-1.5 font-medium text-slate-900">
                    {c.label}
                    {c.backupEligible ? <Badge tone="neutral">Synced passkey</Badge> : null}
                  </p>
                  <p className="text-slate-600">
                    Set up {formatDateTime(c.createdAt)}
                    {c.lastUsedAt ? ` · last used ${formatDateTime(c.lastUsedAt)}` : ' · not used yet'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setRemoving(c)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : credentials.data ? (
          <p className="text-sm text-slate-600">Not set up yet.</p>
        ) : null}

        {available.data ? (
          adding ? (
            <form
              className="max-w-md space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) setUp.mutate();
              }}
            >
              {setUp.error ? <Alert tone="danger">{errorMessage(setUp.error)}</Alert> : null}
              <Field id="webauthn-name" label="Name of this device" hint="For example “My phone”. Your device then asks for your fingerprint, face or PIN." required>
                {(a11y) => <Input {...a11y} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />}
              </Field>
              <div className="flex gap-2">
                <Button type="submit" loading={setUp.isPending} disabled={!name.trim()}>
                  Set up
                </Button>
                <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <Button variant="outline" onClick={() => setAdding(true)}>
              <Plus aria-hidden /> Set up on this device
            </Button>
          )
        ) : null}

        {removed.length > 0 ? (
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-700">Removed ({removed.length})</summary>
            <ul className="mt-2 space-y-1 text-slate-600">
              {removed.map((c) => (
                <li key={c.id}>
                  {c.label} — removed {c.revokedAt ? formatDateTime(c.revokedAt) : ''}
                  {c.revokeReason ? ` (${c.revokeReason})` : ''}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </CardContent>

      <Dialog open={removing !== null} onOpenChange={(open) => (open ? null : setRemoving(null))}>
        {removing ? (
          <DialogContent title="Remove this sign-in?" description={`“${removing.label}” will no longer confirm your clock events. You can set it up again later.`} size="sm">
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button variant="destructive" loading={remove.isPending} onClick={() => remove.mutate(removing.id)}>
                Remove
              </Button>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </Card>
  );
}
